<template>
  <template v-if="show">
    <Teleport to="#vp-teleport-vp-modal">
      <div
        v-if="showBackdrop"
        class="vp-modal-backdrop"
        :style="{ zIndex: 30000 * zIndexLevel }"
        aria-hidden="true"
        @click.prevent="onBackdropClick"
      />
      <div
        v-bind="$attrs"
        :class="['vp-modal', computedClass]"
        :style="computedStyle"
      >
        <div class="vp-modal-wrapper">
          <slot name="header">
            <div
              v-if="showCloseButton"
              class="flex justify-end"
            >
              <VPButton
                variant="text"
                size="xs"
                class="translate-x-4 pr-6"
                @click.prevent="closeModal"
              >
                Close
              </VPButton>
            </div>
            <div
              v-if="title"
              class="flex justify-center my-2"
            >
              <h1 class="text-vp-base-900 text-center text-3xl">
                {{ title }}
              </h1>
            </div>
          </slot>
          <slot />
          <slot
            v-if="!hideActions"
            name="commands"
            :close="closeModal"
            :confirm="confirmModal"
          >
            <div class="flex flex-col mt-5 sm:flex-row justify-center gap-4">
              <VPButton
                variant="outline"
                class="pl-4 pr-4 w-full"
                size="lg"
                max-width="100%"
                @click.prevent="closeModal"
              >
                {{ closeLabel }}
              </VPButton>
              <VPButton
                class="pl-4 pr-4 w-full"
                size="lg"
                max-width="100%"
                :disabled="confirmActionDisabled"
                @click.prevent="confirmModal"
              >
                {{ confirmLabel }}
              </VPButton>
            </div>
          </slot>
        </div>
      </div>
    </Teleport>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import VPButton from '@/components/form/VPButton.vue';
interface Props {
  show: boolean;
  showCloseButton?: boolean;
  showBackdrop?: boolean;
  backdropClickable?: boolean;
  title?: string;
  closeLabel?: string;
  confirmLabel?: string;
  confirmActionDisabled?: boolean;
  hideActions?: boolean;
  zIndexLevel?: number;
  size?: 'sm' | 'md' | 'lg' | 'full';
}

const props = withDefaults(defineProps<Props>(), {
  show: true,
  showBackdrop: true,
  backdropClickable: true,
  closeLabel: 'Cancel',
  confirmLabel: 'OK',
  zIndexLevel: 1,
  size: 'md',
});

const emit = defineEmits(['update:show', 'confirm', 'close', 'backdropClick']);

const computedClass = computed(() => {
  return [
    {
      'vp-modal-size-sm': props.size === 'sm',
      'vp-modal-size-md': props.size === 'md',
      'vp-modal-size-lg': props.size === 'lg',
      'vp-modal-size-full': props.size === 'full',
    },
  ];
});
const computedStyle = computed(() => {
  return {
    zIndex: 50000 * props.zIndexLevel,
  };
});

function onBackdropClick() {
  if (props.backdropClickable) {
    emit('update:show', false);
    emit('backdropClick');
  }
}

function closeModal() {
  emit('update:show', false);
  emit('close');
}

function confirmModal() {
  // This can be more sophisticated, but for now, just close the modal.
  // We can just emit event to parent component and let it handle the logic.
  // Also we can add loading icon to confirm button.
  emit('update:show', false);
  emit('confirm');
}
</script>
